import {
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  TemplateRef
} from '@angular/core';
import { TooltipConfig, TooltipPosition } from './tooltip.config';
import { tooltipDefaultConfig } from './tooltipDefaultConfig';
import { TooltipService } from './tooltip.service';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[tooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy {
  /**
   * The tooltip content. Can be:
   * - string/number/boolean value for simple text tooltips
   * - true to use the host element's HTML content
   * - TemplateRef for custom template content
   */
  @Input() tooltip!: string | number | boolean | TemplateRef<any> | void;

  @Input() tooltipConfig?: TooltipConfig;
  @Input() tooltipPosition?: TooltipPosition;
  @Input() tooltipDelay?: number;

  /**
   * When true, text in the tooltip will be forced to white
   * regardless of the original element's text color
   */
  @Input() tooltipInvertTextColor: boolean = false;

  private readonly tooltipService = inject(TooltipService);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private tooltipElement?: HTMLElement;
  private tooltipTimer?: ReturnType<typeof setTimeout>;
  private backgroundColor?: string = tooltipDefaultConfig.backgroundColor;
  private textColor?: string = tooltipDefaultConfig.textColor;
  private isBrowser = isPlatformBrowser(this.platformId);

  @HostListener('mouseenter')
  public onMouseEnter() {
    if (!this.tooltip || !this.isBrowser) return;

    // Get the current delay from the tooltip service
    const delay = this.tooltipService.currentDelay();

    // Schedule using the dynamic delay
    this.tooltipTimer = setTimeout(() => this.showTooltip(), delay);

    // Put system into "warm" mode so subsequent items are faster
    this.tooltipService.bumpWarm();
  }

  @HostListener('mouseleave')
  public onMouseLeave() {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
    }
    this.hideTooltip();
  }

  public ngOnInit(): void {
    if (!this.tooltip || !this.isBrowser) return;

    if (this.tooltipDelay === undefined) {
      this.tooltipDelay = this.tooltipConfig?.delay ?? tooltipDefaultConfig.delay;
    }
    if (this.tooltipPosition === undefined) {
      this.tooltipPosition = this.tooltipConfig?.position ?? tooltipDefaultConfig.position;
    }

    this.backgroundColor = this.tooltipConfig?.backgroundColor ?? tooltipDefaultConfig.backgroundColor;
    this.textColor = this.tooltipConfig?.textColor ?? tooltipDefaultConfig.textColor;

    // Listen for scroll events to reposition the tooltip
    if (this.isBrowser) {
      fromEvent(window, 'scroll', { capture: true })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.tooltipElement) {
            this.positionTooltip();
          }
        });

      fromEvent(window, 'resize')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.tooltipElement) {
            this.positionTooltip();
          }
        });
    }
  }

  public ngOnDestroy(): void {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
    }
    this.destroyTooltip();
  }

  private showTooltip(): void {
    if (this.tooltipElement || !this.isBrowser) return;

    // Create tooltip element
    this.tooltipElement = this.renderer.createElement('div');

    // Add classes for styling
    this.renderer.addClass(this.tooltipElement, 'fixed');
    this.renderer.addClass(this.tooltipElement, 'rounded-md');
    this.renderer.addClass(this.tooltipElement, 'text-sm');
    this.renderer.addClass(this.tooltipElement, this.backgroundColor ?? 'bg-black');
    this.renderer.addClass(this.tooltipElement, this.textColor ?? 'text-white');
    this.renderer.addClass(this.tooltipElement, 'p-2');
    this.renderer.addClass(this.tooltipElement, 'z-[9999]');
    this.renderer.addClass(this.tooltipElement, 'shadow');
    this.renderer.addClass(this.tooltipElement, 'select-none');
    this.renderer.addClass(this.tooltipElement, 'pointer-events-none');

    // Size handling
    this.renderer.addClass(this.tooltipElement, 'w-max');
    this.renderer.addClass(this.tooltipElement, 'max-w-xs');
    this.renderer.addClass(this.tooltipElement, 'whitespace-normal');
    this.renderer.addClass(this.tooltipElement, 'break-words');

    // Transitions
    this.renderer.addClass(this.tooltipElement, 'transition-all');
    this.renderer.addClass(this.tooltipElement, 'duration-200');
    this.renderer.addClass(this.tooltipElement, 'ease-out');

    // Accessibility
    this.renderer.setAttribute(this.tooltipElement, 'role', 'tooltip');

    // Handle reduced motion preference
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.renderer.setStyle(this.tooltipElement, 'transition', 'none');
    }

    // Start with 0 opacity for fade-in
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');

    // Set content based on input type
    this.setTooltipContent();

    // Add to body
    this.renderer.appendChild(document.body, this.tooltipElement);

    // Position the tooltip relative to the host element
    this.positionTooltip();

    // Fade in
    requestAnimationFrame(() => {
      if (!this.tooltipElement) return;
      this.renderer.setStyle(this.tooltipElement, 'opacity', '1');
    });
  }

  private setTooltipContent(): void {
    if (!this.tooltipElement) return;

    // Check the type of tooltip content
    if (this.tooltip === true) {
      // Clone the host element's content
      const clonedContent = this.el.nativeElement.cloneNode(true) as HTMLElement;

      if (this.tooltipInvertTextColor) {
        // Force white text in the cloned content and its children
        this.applyWhiteTextRecursively(clonedContent);
      } else {
        // Apply styles to match original appearance
        this.copyComputedStyles(this.el.nativeElement, clonedContent);
      }

      // Clear any event listeners, ids, etc.
      clonedContent.id = '';
      clonedContent.removeAttribute('tooltip');

      // Append the cloned content to tooltip
      this.renderer.appendChild(this.tooltipElement, clonedContent);
    } else if (this.tooltip instanceof TemplateRef) {
      // Not implemented in this version - would require ViewContainerRef
      // For simple case, just show as text
      const text = this.renderer.createText('Template tooltips not supported in this version');
      this.renderer.appendChild(this.tooltipElement, text);
    } else {
      // Simple string/number/boolean value
      const text = this.renderer.createText(String(this.tooltip));
      this.renderer.appendChild(this.tooltipElement, text);
    }
  }

  private applyWhiteTextRecursively(element: HTMLElement): void {
    // Force white text color
    this.renderer.setStyle(element, 'color', 'white');

    // Apply to all children recursively
    for (let i = 0; i < element.children.length; i++) {
      this.applyWhiteTextRecursively(element.children[i] as HTMLElement);
    }
  }

  private copyComputedStyles(source: HTMLElement, target: HTMLElement): void {
    // Get computed styles
    const computedStyle = window.getComputedStyle(source);

    // Copy relevant styles - skip text color if we're using inverted text
    const stylesToCopy = [
      this.tooltipInvertTextColor ? '' : 'color',
      'background-color', 'font-size', 'font-weight',
      'font-style', 'text-transform', 'text-decoration'
    ].filter(Boolean);

    stylesToCopy.forEach(style => {
      target.style.setProperty(style, computedStyle.getPropertyValue(style));
    });

    // Copy styles recursively for child elements
    for (let i = 0; i < source.children.length; i++) {
      if (i < target.children.length) {
        this.copyComputedStyles(
          source.children[i] as HTMLElement,
          target.children[i] as HTMLElement
        );
      }
    }
  }

  private positionTooltip(): void {
    if (!this.tooltipElement || !this.isBrowser) return;

    // Get element position
    const rect = this.el.nativeElement.getBoundingClientRect();

    // Wait for tooltip to be in DOM and rendered
    requestAnimationFrame(() => {
      if (!this.tooltipElement) return;

      const tooltipRect = this.tooltipElement.getBoundingClientRect();

      // Calculate available space in each direction
      const spaceTop = rect.top;
      const spaceBottom = window.innerHeight - rect.bottom;
      const spaceLeft = rect.left;
      const spaceRight = window.innerWidth - rect.right;

      // Determine best position if auto
      let position = this.tooltipPosition || 'top';

      // Auto-flip based on available space
      if (position === 'top' && spaceTop < tooltipRect.height && spaceBottom > tooltipRect.height) {
        position = 'bottom';
      } else if (position === 'bottom' && spaceBottom < tooltipRect.height && spaceTop > tooltipRect.height) {
        position = 'top';
      } else if (position === 'left' && spaceLeft < tooltipRect.width && spaceRight > tooltipRect.width) {
        position = 'right';
      } else if (position === 'right' && spaceRight < tooltipRect.width && spaceLeft > tooltipRect.width) {
        position = 'left';
      }

      // Position based on final direction
      const gap = 8; // Gap between element and tooltip

      let top, left;

      switch (position) {
        case 'top':
          top = rect.top - tooltipRect.height - gap;
          left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
          this.renderer.setStyle(this.tooltipElement, 'transform', 'translateY(0)');
          break;
        case 'bottom':
          top = rect.bottom + gap;
          left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
          this.renderer.setStyle(this.tooltipElement, 'transform', 'translateY(0)');
          break;
        case 'left':
          top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
          left = rect.left - tooltipRect.width - gap;
          this.renderer.setStyle(this.tooltipElement, 'transform', 'translateX(0)');
          break;
        case 'right':
          top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
          left = rect.right + gap;
          this.renderer.setStyle(this.tooltipElement, 'transform', 'translateX(0)');
          break;
      }

      // Constrain to viewport
      top = Math.max(8, Math.min(window.innerHeight - tooltipRect.height - 8, top));
      left = Math.max(8, Math.min(window.innerWidth - tooltipRect.width - 8, left));

      // Apply position
      this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
      this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    });
  }

  private hideTooltip(): void {
    if (!this.tooltipElement) return;

    const tooltip = this.tooltipElement;

    // Fade out
    this.renderer.setStyle(tooltip, 'opacity', '0');

    // Remove after animation
    const transitionEndHandler = () => {
      tooltip.removeEventListener('transitionend', transitionEndHandler);
      this.destroyTooltip();
    };

    tooltip.addEventListener('transitionend', transitionEndHandler);

    // Fallback in case transitionend doesn't fire
    setTimeout(() => this.destroyTooltip(), 300);
  }

  private destroyTooltip(): void {
    if (this.tooltipElement && document.body.contains(this.tooltipElement)) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = undefined;
    }
  }
}
